import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import orderBy from 'lodash/orderBy';

import Spinner from 'Shared/components/Spinner';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { showNotificationError } from 'Shared/helpers';
import { getRecurringMeetings } from 'Shared/actions/user';
import { getRecurringMeetingWithoutWorkspaces } from 'Shared/selectors/user';

import MeetingsListItem from '../../MeetingsListItem/MeetingsListItem';

import './GroupSuggestionsCalendarConnected.scss';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

const keyWords = ['team', 'all hands'];

type Props = {
  onWorkspaceNameSet: Function;
  onInviteesChange: Function;
  onSetRecurringMeetingId: Function;
  onSetIsSuggestionOpened: Function;
};

function GroupSuggestionsCalendarConnected({
  onWorkspaceNameSet,
  onInviteesChange,
  onSetRecurringMeetingId,
  onSetIsSuggestionOpened,
}: Props) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { tr } = useTranslation();

  const recurringMeetingsWithoutWorkspaces = useDeepEqualSelector(getRecurringMeetingWithoutWorkspaces());

  const suggestedList = () => {
    const listWithScore = recurringMeetingsWithoutWorkspaces.map(meeting => {
      let score;
      if (keyWords.some(keyWord => meeting.name.toLowerCase().includes(keyWord))) {
        score = 0;
      } else {
        score = Math.abs(meeting.attendees.length - 4) + 1;
      }
      return { score, meeting };
    });
    const orderedList = orderBy(listWithScore, 'score');
    return orderedList.slice(0, 4).map(item => item.meeting);
  };

  const listOfSuggests = suggestedList();

  useEffect(() => {
    async function fetchRecurringMeetings() {
      try {
        setIsLoading(true);
        await dispatch(getRecurringMeetings());
      } catch (e) {
        showNotificationError(e);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecurringMeetings();
  }, []);

  const handleItemClick = async ({ id, name, emails }) => {
    const emailsOptions = emails.map(email => ({ value: email, label: email, fromCalendar: true }));
    onWorkspaceNameSet(name);
    onInviteesChange(emailsOptions);
    onSetRecurringMeetingId(id);
    onSetIsSuggestionOpened(false);
  };

  if (!listOfSuggests.length) return null;

  return (
    <>
      <div className="add-group-sugestions-section-title pb-2">
        {tr('perform.simple_add_workspace.suggestions.group.connected.title')}
      </div>
      {isLoading ? (
        <div className="group-suggestions-calendar-connected-spinner w-100 p-4">
          <Spinner activeIf />
        </div>
      ) : (
        <div className="group-suggestions-calendar-connected scrollbar">
          {listOfSuggests.map(meeting => (
            <MeetingsListItem
              key={meeting.id}
              id={meeting.id}
              name={meeting.name}
              attendees={meeting.attendees}
              onClick={handleItemClick}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default GroupSuggestionsCalendarConnected;
